import React, { useState } from "react";
import { Send, Sparkles, Copy, Check, RefreshCw, Calendar, CheckCircle2, ShieldCheck, Mail, Phone, MessageSquare, UserCheck, AlertTriangle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../../config/featureFlags";

interface OutreachAssistantToolProps {
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  savedEmployers?: any[];
  userPreferences?: any;
  onBusinessCrmUpdate?: (businessId: string, stage: string, followUpAt: string | null) => void;
  preSelectedEmployer?: any;
  theme?: "light" | "dark";
  providerHealth?: { status: string; outageReason?: string | null };
  onProviderError?: (err: any) => void;
}

export default function OutreachAssistantTool({
  apiFetch,
  savedEmployers = [],
  userPreferences,
  onBusinessCrmUpdate,
  preSelectedEmployer,
  theme = "light",
  providerHealth,
  onProviderError
}: OutreachAssistantToolProps) {
  const isDark = theme === "dark";
  const isCircuitOpen = providerHealth?.status === "outage";

  const [selectedEmployerId, setSelectedEmployerId] = useState<string>(
    preSelectedEmployer?.id || savedEmployers[0]?.id || "custom"
  );

  const [channel, setChannel] = useState<"email" | "phone" | "form" | "in_person" | "follow_up">("email");
  const [tone, setTone] = useState<"conversational" | "formal" | "direct" | "enthusiastic">("conversational");

  const [customName, setCustomName] = useState(preSelectedEmployer?.name || "");
  const [userSkills, setUserSkills] = useState(
    Array.isArray(userPreferences?.skills) ? userPreferences.skills.join(", ") : ""
  );
  const [userExperience, setUserExperience] = useState(userPreferences?.experience || "");
  const [userGoal, setUserGoal] = useState("Inquire about current or upcoming job openings");
  const [priorOutreach, setPriorOutreach] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);

  const [editedText, setEditedText] = useState("");
  const [copied, setCopied] = useState(false);
  const [sentStatus, setSentStatus] = useState<string | null>(null);
  const [followUpDate, setFollowUpDate] = useState("");

  const handleGenerateOutreach = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading || isCircuitOpen) return;

    if (!AI_FEATURES_ENABLED) {
      setError("AI features are currently disabled.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setCopied(false);
    setSentStatus(null);

    let employerData: any = {};
    if (selectedEmployerId !== "custom") {
      const found = savedEmployers.find((e) => String(e.id) === String(selectedEmployerId));
      if (found) {
        employerData = {
          name: found.name,
          category: found.category || found.type,
          website: found.website || found.url,
          phone: found.phone || found.formatted_phone_number,
          jobTitle: found.jobTitle || ""
        };
      }
    } else {
      employerData = {
        name: customName || "Target Employer",
        category: "Local Business"
      };
    }

    try {
      const res = await apiFetch("/api/ai/outreach-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel,
          tone,
          employer: employerData,
          user: {
            name: userPreferences?.name || "Job Candidate",
            skills: userSkills.split(",").map((s) => s.trim()).filter(Boolean),
            experience: userExperience,
            goal: userGoal
          },
          priorOutreach,
          customInstructions
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setResult(data.data);
      if (channel === "phone" && data.data?.phoneScript) {
        const ps = data.data.phoneScript;
        setEditedText(`OPENING:\n${ps.opening}\n\nREASON:\n${ps.reasonForCalling}\n\nEXPERIENCE:\n${ps.experienceSummary}\n\nQUESTIONS TO ASK:\n${(ps.questionsToAsk || []).join("\n")}\n\nVOICEMAIL:\n${ps.voicemailScript}`);
      } else {
        setEditedText(data.data?.primaryMessage || "");
      }
    } catch (err: any) {
      console.error("Outreach Assistant Error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    let contentToCopy = editedText;
    if (result?.subject && channel === "email") {
      contentToCopy = `Subject: ${result.subject}\n\n${editedText}`;
    }
    navigator.clipboard.writeText(contentToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleMarkSent = () => {
    if (selectedEmployerId !== "custom" && onBusinessCrmUpdate) {
      onBusinessCrmUpdate(selectedEmployerId, "Contacted", followUpDate || null);
    }
    setSentStatus("Outreach marked as SENT and updated in CRM pipeline!");
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <Send className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">AI Outreach Assistant</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Generates truthful, individualized email, phone, form, and in-person scripts grounded exclusively in your real experience.
            </p>
          </div>
        </div>

        {/* Truthfulness Notice */}
        <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300 font-medium">
          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
          <span>
            <strong>Truthfulness Guaranteed:</strong> The AI will only use skills and experience you provide below. It will never invent resume bullets, false achievements, or fake metrics.
          </span>
        </div>

        <form onSubmit={handleGenerateOutreach} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Target Employer</label>
              <select
                disabled={isLoading || isCircuitOpen}
                value={selectedEmployerId}
                onChange={(e) => setSelectedEmployerId(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savedEmployers.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} — {emp.category || "Saved Business"}
                  </option>
                ))}
                <option value="custom">+ Custom Business Name</option>
              </select>
            </div>

            {selectedEmployerId === "custom" && (
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Business Name</label>
                <input
                  type="text"
                  disabled={isLoading || isCircuitOpen}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Apex Health Clinic"}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Outreach Channel</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                {[
                  { id: "email", label: "Email", icon: Mail },
                  { id: "phone", label: "Phone", icon: Phone },
                  { id: "form", label: "Form", icon: MessageSquare },
                  { id: "in_person", label: "In-Person", icon: UserCheck },
                  { id: "follow_up", label: "Follow-Up", icon: Calendar }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={isLoading || isCircuitOpen}
                      onClick={() => setChannel(item.id as any)}
                      className={`p-2 rounded-xl text-[11px] font-bold flex flex-col items-center justify-center gap-1 transition cursor-pointer border disabled:opacity-60 disabled:cursor-not-allowed ${
                        channel === item.id
                          ? "bg-indigo-600 text-white border-indigo-700 shadow-xs"
                          : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Tone</label>
              <select
                disabled={isLoading || isCircuitOpen}
                value={tone}
                onChange={(e) => setTone(e.target.value as any)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="conversational">Conversational & Warm</option>
                <option value="formal">Formal & Professional</option>
                <option value="direct">Direct & Concise</option>
                <option value="enthusiastic">Enthusiastic & Driven</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Your Real Experience & Relevant Skills
              </label>
              <input
                type="text"
                disabled={isLoading || isCircuitOpen}
                value={userSkills}
                onChange={(e) => setUserSkills(e.target.value)}
                placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. 2 years customer service, medical receptionist, scheduling, phone management"}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || isCircuitOpen}
            className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Generating Truthful Script...</span>
              </>
            ) : isCircuitOpen ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-300" />
                <span>AI Service Temporarily Unavailable</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>Generate Outreach Draft</span>
              </>
            )}
          </button>
        </form>
      </div>

      {error && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-950 dark:text-amber-100 shadow-xs">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0 flex-1">
            <p className="font-bold text-amber-900 dark:text-amber-200">AI Service Notice</p>
            <p className="leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      {/* Generated Result Display */}
      {result && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-3">
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 block">
                {channel.toUpperCase()} OUTREACH DRAFT
              </span>
              {result.subject && channel === "email" && (
                <div className="text-xs font-bold text-slate-900 dark:text-white mt-1">
                  Subject: <span className="font-extrabold text-indigo-600 dark:text-indigo-300">{result.subject}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="px-3.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-800 dark:text-slate-200 font-bold text-xs rounded-lg transition flex items-center gap-1.5 cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? "Copied!" : "Copy Text"}</span>
              </button>
            </div>
          </div>

          {/* Text Editor */}
          <div>
            <textarea
              rows={10}
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs font-mono text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed"
            />
          </div>

          {/* Action Footer */}
          <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Suggested Follow-Up:</span>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-white"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleMarkSent}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Mark as Sent</span>
              </button>
            </div>
          </div>

          {sentStatus && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-200 font-extrabold">
              {sentStatus}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
